import { jwtDecode } from "jwt-decode";
import {
	endSession,
	getValidAccessToken,
	getValidIdToken,
	hasSession,
	RefreshUnavailableError,
	SessionExpiredError,
} from "../auth/tokenManager";

/**
 * Drop-in replacement for fetch on calls that carry an Authorization header.
 *
 * Two things it guarantees that plain fetch did not:
 *   1. The token on the wire is always fresh — it is resolved at call time from
 *      the token manager, never from a value captured when the page mounted.
 *   2. The request cannot hang forever. Every call is on a timeout, so a
 *      stalled connection surfaces as an error instead of a spinner that
 *      never stops.
 */

/* Long enough for a slow mobile upload, short enough that a dead connection
   turns into a visible error rather than an endless loader. */
const DEFAULT_TIMEOUT_MS = 30000;

function authHeaderKey(headers) {
	return Object.keys(headers).find((k) => k.toLowerCase() === "authorization");
}

function bearerOf(headers) {
	const key = authHeaderKey(headers);
	if (!key) return null;
	const value = headers[key];
	if (typeof value !== "string") return null;
	return value.replace(/^Bearer\s+/i, "").trim() || null;
}

/**
 * Some endpoints are authenticated with the ID token rather than the access
 * token. Read the intent off the token the caller handed us so behaviour stays
 * identical per call site.
 */
function tokenKindOf(token) {
	if (!token) return "access";
	try {
		return jwtDecode(token).token_use === "id" ? "id" : "access";
	} catch {
		return "access";
	}
}

function resolveToken(kind, forceRefresh) {
	const get = kind === "id" ? getValidIdToken : getValidAccessToken;
	return get({ forceRefresh });
}

function looksEmpty(token) {
	return !token || token === "null" || token === "undefined";
}

function fetchWithTimeout(url, options, timeoutMs) {
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), timeoutMs);

	/* Respect a caller-supplied signal alongside our timeout. */
	if (options.signal) {
		if (options.signal.aborted) controller.abort();
		else options.signal.addEventListener("abort", () => controller.abort(), { once: true });
	}

	/* The timer is deliberately left running once headers arrive: aborting also
	   covers reading the body, and a stalled body is exactly the case that used
	   to leave a spinner up forever. Firing abort on an already-finished
	   request is a no-op. */
	return fetch(url, { ...options, signal: controller.signal }).catch((err) => {
		clearTimeout(timer);
		throw err;
	});
}

export async function authFetch(url, options = {}) {
	const { timeoutMs = DEFAULT_TIMEOUT_MS, auth, ...rest } = options;
	const headers = { ...(rest.headers || {}) };

	const headerKey = authHeaderKey(headers);
	const suppliedToken = bearerOf(headers);
	const needsAuth = auth === true || Boolean(headerKey);

	/* Public endpoints (login, register, discover…) pass straight through, with
	   a timeout added. */
	if (!needsAuth) {
		return fetchWithTimeout(url, rest, timeoutMs);
	}

	const key = headerKey || "Authorization";

	if (!hasSession()) {
		/* Guest browsing, or signed out mid-flow. Send the request rather than
		   inventing an auth failure — but drop a "Bearer undefined" so the
		   server sees a clean anonymous call. */
		const clean = { ...headers };
		if (looksEmpty(suppliedToken)) delete clean[key];
		return fetchWithTimeout(url, { ...rest, headers: clean }, timeoutMs);
	}

	const kind = tokenKindOf(suppliedToken);

	const send = async (token) =>
		fetchWithTimeout(url, { ...rest, headers: { ...headers, [key]: `Bearer ${token}` } }, timeoutMs);

	let token = await resolveToken(kind, false);
	let res = await send(token);

	if (res.status !== 401) return res;

	/* The proactive refresh should make this rare — it covers clock skew and
	   tokens revoked server-side. One forced retry, then give up. */
	try {
		token = await resolveToken(kind, true);
	} catch (err) {
		if (err instanceof RefreshUnavailableError) return res;
		throw err;
	}

	res = await send(token);

	/* A demonstrably fresh access token that the API still rejects means the
	   session is dead server-side. Only access tokens are treated that way: the
	   API only accepts access tokens, so a 401 on an ID-token call says nothing
	   about the session and must not sign anyone out. */
	if (res.status === 401 && kind === "access") {
		endSession("rejected-after-refresh");
		throw new SessionExpiredError("Session is no longer valid");
	}

	return res;
}

/**
 * Back-compat wrapper for the old signature. The userData argument is ignored:
 * tokens now come from the token manager, not from a React snapshot.
 */
export function fetchWithRefresh(url, options = {}, _userData) {
	return authFetch(url, { ...options, auth: true });
}

export default authFetch;
